import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FullCalendarModule } from '@fullcalendar/angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ConfirmationService, MessageService } from 'primeng/api';
import { NgIconsModule, provideIcons } from '@ng-icons/core';
import { 
  heroBars3Solid,
  heroMagnifyingGlassSolid,
  heroBellSolid,
  heroQuestionMarkCircleSolid,
  heroGlobeAltSolid,
  heroSquares2x2Solid,
  heroUserSolid,
  heroCog6ToothSolid,
  heroArrowRightOnRectangleSolid,
  heroChevronDownSolid,
  heroPlusSolid,
  heroUsersSolid,
  heroFolderSolid,
  heroDocumentTextSolid,
  heroChartBarSolid,
  heroBuildingOfficeSolid,
  heroBriefcaseSolid,
  heroCalculatorSolid,
  heroCurrencyDollarSolid,
  heroClipboardDocumentCheckSolid,
  heroUserGroupSolid,
  heroCalendarDaysSolid,
  heroClockSolid,
  heroShieldCheckSolid,
  heroBanknotesSolid,
  heroDocumentChartBarSolid,
  heroPresentationChartLineSolid,
  heroWrenchScrewdriverSolid,
  heroAcademicCapSolid,
  heroPencilSquareSolid,
  heroEyeSolid,
  heroTrashSolid,
  heroPaperAirplaneSolid,
  heroChevronLeftSolid,
  heroChevronRightSolid,
  heroMagnifyingGlassCircleSolid,
  heroUserCircleSolid,
  heroArrowTrendingUpSolid,
  heroCakeSolid,
  heroUserPlusSolid,
  heroMapPinSolid,
  heroFunnelSolid,
  heroXMarkSolid,
  heroEllipsisVerticalSolid,
  heroBarsArrowUpSolid,
  heroListBulletSolid,
  heroCloudArrowUpSolid,
  heroTableCellsSolid,
  heroEllipsisHorizontalSolid,
  heroArrowPathSolid,
  heroArrowRightSolid,
  heroExclamationTriangleSolid
} from '@ng-icons/heroicons/solid';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DataViewModule } from 'primeng/dataview';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { DialogService } from 'primeng/dynamicdialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { OrganizationChartModule } from 'primeng/organizationchart';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { DrawerModule } from 'primeng/drawer';
import { SplitButtonModule } from 'primeng/splitbutton';
import { MenuModule } from 'primeng/menu';
import { UniTabsModule } from 'src/app/shared/components/uni-tabs/uni-tabs.module';
import { TimelineModule } from 'primeng/timeline';
import { ToastModule } from 'primeng/toast';
import { AppConfigService, appConfigProvide } from 'src/config/app-config.service';
import { AuthConfigModule } from 'src/config/auth.config.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthCallbackComponent } from './auth-callback/auth-callback.component';
import { ListGridAngularModule } from './common/list-grid-angular/list-grid-angular.module';
import { UniNotFoundComponent } from './components/uni-not-found/uni-not-found.component';
import { DefaultLayoutComponent } from './containers/default-layout';
import { LayoutQuanLyTuyenDungComponent } from './containers/layout-quan-ly-tuyen-dung/layout-quan-ly-tuyen-dung.component';
import { HomeComponent } from './pages/home/home.component';
import { ApiCoreService } from './services/api-core/apicore.service';
import { ApiHrmService } from './services/api-hrm/apihrm.service';
import { ApiHrmV2Service } from './services/api-hrm/apihrmv2.service';
import { ApiService } from './services/api.service';
import { AuthGuardService } from './services/auth-guard.service';
import { AuthInterceptor } from './services/auth-interceptor';
import { AuthService } from './services/auth.service';
import { EmployeeSaveService } from './services/employee-save.service';
import { ErrorService } from './services/error.service';
import { ExportFileService } from './services/export-file.service';
import { FeedBaseService } from './services/firebase.service';
import { NavbarService } from './services/navbar.service';
import { NavigationService } from './services/navigation.service';
import { NotificationService } from './services/notification.service';
import { OrganizeInfoService } from './services/organize-info.service';
import { ExcelModule } from './shared/components/excel/excel.module';
import { SharedModule } from './shared/shared.module';
import { SunshineComponent } from './sunshine/sunshine.component';
import { CheckboxEditorComponent } from './utils/common/checkbox-editor.component';
import { CurrencyDirectiveModule } from './utils/common/currency.module';
import { DropdownRendererComponent } from './utils/common/dropdown-renderer.component';
import { NumberCellRenderer } from './utils/common/number-renderer.component';
import { TextEditorComponent } from './utils/common/text-editor.component';
import { TooltipSuggestionComponent } from './utils/common/tooltip-suggestion.component';
import { providePrimeNG } from 'primeng/config';
import { TooltipModule } from 'primeng/tooltip';
import { BluePreset } from './config/noir-preset';
// import { AccordionModule } from 'primeng/accordion'; // Replaced by custom AccordionModule
import { AccordionModule } from './common-component/formLy/nz-accordion/accordion.module';
import { AgChartsModule } from 'ag-charts-angular';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import 'prismjs';
import 'prismjs/components/prism-typescript.min.js';
import 'prismjs/plugins/line-numbers/prism-line-numbers.js';
import 'prismjs/plugins/line-highlight/prism-line-highlight.js';
import { MarkdownModule } from 'ngx-markdown';

const APP_CONTAINERS = [DefaultLayoutComponent, LayoutQuanLyTuyenDungComponent];
export function HttpLoaderFactory(httpClient: HttpClient) {
    return new TranslateHttpLoader(httpClient);
}
export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}
@NgModule({
    declarations: [
        AppComponent,
        ...APP_CONTAINERS,
        AuthCallbackComponent,
        SunshineComponent,
        HomeComponent,
        NumberCellRenderer,
        TextEditorComponent,
        CheckboxEditorComponent,
        TooltipSuggestionComponent,
        DropdownRendererComponent,
        UniNotFoundComponent
    ],
    bootstrap: [AppComponent],
    imports: [
        BrowserAnimationsModule,
        DrawerModule,
        AccordionModule,
        AppRoutingModule,
        SharedModule,
        FormsModule,
        ReactiveFormsModule,
        BrowserModule,
        ButtonModule,
        DialogModule,
        CurrencyDirectiveModule,
        ConfirmDialogModule,
        SplitButtonModule,
        ScrollPanelModule,
        NgxSpinnerModule,
        DataViewModule,
        ListGridAngularModule,
        BreadcrumbModule,
        OrganizationChartModule,
        MultiSelectModule,
        DrawerModule,
        AvatarModule,
        TooltipModule,
        MarkdownModule.forRoot(),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        ExcelModule,
        ToastModule,
        ChartModule,
        MenuModule,
        SelectModule,
        DatePickerModule,
        AutoCompleteModule,
        CardModule,
        TimelineModule,
        FullCalendarModule,
        UniTabsModule,
        AvatarModule,
        AvatarGroupModule,
        AuthConfigModule,
        AgChartsModule,
        NgIconsModule
    ],
    providers: [
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: BluePreset,
                options: {
                    darkModeSelector: '.dark'
                }
            },
            zIndex: {
                modal: 10050,    // dialog, sidebar, drawer - baseZIndex cho drawer (mask = 10050, drawer root = 10051)
                overlay: 1000,   // dropdown, overlaypanel
                menu: 1000,      // overlay menus
                tooltip: 1100    // tooltip
            }
        }),
        provideIcons({
            heroBars3Solid,
            heroMagnifyingGlassSolid,
            heroBellSolid,
            heroQuestionMarkCircleSolid,
            heroGlobeAltSolid,
            heroSquares2x2Solid,
            heroUserSolid,
            heroCog6ToothSolid,
            heroArrowRightOnRectangleSolid,
            heroChevronDownSolid,
            heroPlusSolid,
            heroUsersSolid,
            heroFolderSolid,
            heroDocumentTextSolid,
            heroChartBarSolid,
            heroBuildingOfficeSolid,
            heroBriefcaseSolid,
            heroCalculatorSolid,
            heroCurrencyDollarSolid,
            heroClipboardDocumentCheckSolid,
            heroUserGroupSolid,
            heroCalendarDaysSolid,
            heroClockSolid,
            heroShieldCheckSolid,
            heroBanknotesSolid,
            heroDocumentChartBarSolid,
            heroPresentationChartLineSolid,
            heroWrenchScrewdriverSolid,
            heroAcademicCapSolid,
            heroPencilSquareSolid,
            heroEyeSolid,
            heroTrashSolid,
            heroPaperAirplaneSolid,
            heroChevronLeftSolid,
            heroChevronRightSolid,
            heroMagnifyingGlassCircleSolid,
            heroUserCircleSolid,
            heroArrowTrendingUpSolid,
            heroCakeSolid,
            heroUserPlusSolid,
            heroMapPinSolid,
            heroFunnelSolid,
            heroXMarkSolid,
            heroEllipsisVerticalSolid,
            heroBarsArrowUpSolid,
            heroListBulletSolid,
            heroCloudArrowUpSolid,
            heroTableCellsSolid,
            heroEllipsisHorizontalSolid,
            heroArrowPathSolid,
            heroArrowRightSolid,
            heroExclamationTriangleSolid
        }),
        {
            provide: APP_INITIALIZER,
            deps: [
                AppConfigService
            ],
            multi: true,
            useFactory: appConfigProvide
        },
        AppConfigService,
        AuthService,
        AuthGuardService,
        ExportFileService,
        ApiService,
        ApiHrmService,
        NavbarService,
        ApiCoreService,
        FeedBaseService,
        NotificationService,
        ConfirmationService,
        NavigationService,
        MessageService,
        OrganizeInfoService,
        EmployeeSaveService,
        ErrorService,
        DialogService,
        DatePipe,
        ApiHrmV2Service,
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        provideHttpClient(withInterceptorsFromDi()),
    ]
})
export class AppModule { } 
